import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Service, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import {
  HANDLE_HTTP_ERROR_LOCALLY,
  SKIP_AUTH_TOKEN,
  SKIP_SUCCESS_ALERT,
} from '../../../core/http/http-context-tokens';
import { DepartamentoSicPublicoResponse, PedidoSicAberturaResponse } from '../models/pedido-sic.model';
import { CABECALHO_DESAFIO, CAMPO_HONEYPOT } from './desafio-formulario.service';

const RECURSO = `${environment.apiUrl}/sic/publico`;

/**
 * O erro fica local — cada etapa decide o que mostrar — e, no caminho anônimo, o token NÃO
 * vai: o back-end decide o anonimato pela ausência de identidade, e um cidadão logado que
 * escolheu "solicitar anonimamente" viraria identificado.
 */
function contexto(anonimo: boolean): HttpContext {
  return new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true).set(SKIP_AUTH_TOKEN, anonimo);
}

/**
 * A superfície pública do pedido e-SIC vista pelo portal: os departamentos que a sessão pode
 * escolher e a abertura do pedido. O canal do órgão (contato, responsável e prazos) é do
 * `CanalSicPublicoService` — o canal não é do pedido, e a tela final lê o prazo do `201`.
 *
 * ⚠️ O portal **não sabe nem envia o órgão**: o back-end o resolve pelo host de quem chamou.
 * O `FormData` da abertura nunca escreve `codOrgaoOrg`, protocolo, status, prazo nem
 * demográficos — lá são campos do sistema, e mandá-los devolve 400 nomeando o campo.
 */
@Service()
export class PedidoSicPublicoService {
  private readonly http = inject(HttpClient);

  /**
   * Os departamentos que a sessão pode escolher. A lista depende da sessão, e quem a decide é
   * o servidor: no anônimo a requisição sai sem token e volta sem os que exigem login.
   */
  departamentos(anonimo: boolean): Observable<DepartamentoSicPublicoResponse[]> {
    return this.http
      .get<ResponseApi<DepartamentoSicPublicoResponse[]>>(`${RECURSO}/pedido/departamentos`, {
        context: contexto(anonimo),
      })
      .pipe(map((resposta) => resposta.data ?? []));
  }

  /**
   * Abre o pedido. O alerta automático de sucesso é silenciado: a tela de confirmação **é** a
   * resposta ao cidadão. Sem `Content-Type` manual — quem põe o boundary é o navegador.
   *
   * O `desafio` de formulário vai no cabeçalho `X-Desafio-Formulario` — no anônimo **e** no
   * identificado: a sessão não substitui o desafio (change anti-abuso-superficie-publica).
   */
  abrir(corpo: FormData, anonimo: boolean, desafio: string): Observable<PedidoSicAberturaResponse> {
    return this.http
      .post<ResponseApi<PedidoSicAberturaResponse>>(`${RECURSO}/pedido`, corpo, {
        context: contexto(anonimo).set(SKIP_SUCCESS_ALERT, true),
        headers: { [CABECALHO_DESAFIO]: desafio },
      })
      .pipe(map((resposta) => resposta.data));
  }
}

/** O que o `FormData` da abertura carrega. Não há campo do sistema aqui — por construção. */
export interface DadosAberturaPedidoSic {
  anonimo: boolean;
  aceite: boolean;
  codDepartamentoDep: number;
  assunto: string;
  descricao: string;
  /** Só no identificado. */
  identificacao: {
    nome: string;
    cpf: string;
    email: string;
    telefone: string;
    tipoSolicitante: string;
    logradouro: string;
    numero: string;
    complemento: string;
    bairro: string;
    cidade: string;
    uf: string;
    cep: string;
  } | null;
  formaRecebimento: string;
  protocoloVinculado: string;
  receberAvisos: boolean;
  anexos: readonly File[];
  /** O campo invisível do anti-abuso. Vazio para toda pessoa — e então não vai. */
  honeypot?: string;
}

/**
 * Monta o `multipart/form-data` da abertura. Função pura e exportada: é o ponto em que o
 * contrato do back-end é honrado.
 *
 * 1. **só chave com valor** — string vazia é ruído no contrato;
 * 2. no **anônimo**, NENHUM campo de contato, forma, protocolo vinculado nem aviso por e-mail:
 *    o back-end recusa com 400 qualquer um deles numa requisição sem sessão;
 * 3. CPF e CEP vão **sem máscara** — o back-end normaliza de novo, por garantia;
 * 4. um `Anexos` por arquivo, na ordem em que o cidadão os adicionou.
 */
export function montarFormDataPedidoSic(dados: DadosAberturaPedidoSic): FormData {
  const corpo = new FormData();

  const texto = (chave: string, valor: string | null | undefined): void => {
    const limpo = (valor ?? '').trim();

    if (limpo) corpo.append(chave, limpo);
  };

  corpo.append('BitAceiteOrientacoes', dados.aceite ? 'true' : 'false');
  corpo.append('CodDepartamentoDep', String(dados.codDepartamentoDep));
  texto('DesAssunto', dados.assunto);
  texto('DesDescricao', dados.descricao);

  if (!dados.anonimo && dados.identificacao) {
    const id = dados.identificacao;
    const digitos = (valor: string) => valor.replace(/\D/g, '');

    texto('DesNome', id.nome);
    texto('DesCpfCnpj', digitos(id.cpf));
    texto('DesEmail', id.email);
    texto('DesTelefone', id.telefone);
    texto('DesTipoSolicitante', id.tipoSolicitante);
    texto('DesLogradouro', id.logradouro);
    texto('DesNumero', id.numero);
    texto('DesComplemento', id.complemento);
    texto('DesBairro', id.bairro);
    texto('DesCidade', id.cidade);
    texto('DesUf', id.uf);
    texto('DesCep', digitos(id.cep));

    texto('DesFormaRecebimento', dados.formaRecebimento);
    texto('DesProtocoloVinculado', dados.protocoloVinculado);
    corpo.append('BitNotificarEmail', dados.receberAvisos ? 'true' : 'false');
  }

  for (const arquivo of dados.anexos) {
    corpo.append('Anexos', arquivo, arquivo.name);
  }

  // O honeypot segue a regra 1: só preenchido — e preenchido, a API recusa o envio.
  texto(CAMPO_HONEYPOT, dados.honeypot);

  return corpo;
}

/** A etapa do wizard a que um erro do servidor pertence. */
export type EtapaDoErroSic = 'orientacoes' | 'identificacao' | 'dados' | 'anexos';

const CAMPOS_POR_ETAPA: readonly [EtapaDoErroSic, readonly string[]][] = [
  ['orientacoes', ['bitAceiteOrientacoes']],
  [
    'identificacao',
    [
      'desNome', 'desCpfCnpj', 'desEmail', 'desTelefone', 'desTipoSolicitante', 'desLogradouro',
      'desNumero', 'desComplemento', 'desBairro', 'desCidade', 'desUf', 'desCep',
    ],
  ],
  [
    'dados',
    ['codDepartamentoDep', 'desAssunto', 'desDescricao', 'desFormaRecebimento', 'desProtocoloVinculado', 'bitNotificarEmail'],
  ],
];

/**
 * A etapa de um erro de validação do servidor. As mensagens da API nomeiam o campo entre
 * aspas (`'desBairro'`); as de anexo nomeiam o arquivo; as de departamento e de protocolo
 * vinculado são frases fixas. O que não se reconhece fica nos dados do pedido, que é onde o
 * cidadão confere o que enviou.
 */
export function etapaDoErroSic(mensagem: string): EtapaDoErroSic {
  for (const [etapa, campos] of CAMPOS_POR_ETAPA) {
    if (campos.some((campo) => mensagem.includes(`'${campo}'`))) return etapa;
  }

  if (/arquivo|anexo/i.test(mensagem)) return 'anexos';

  return 'dados';
}

/** As mensagens de um erro HTTP da abertura, no formato do envelope `ResponseApi`. */
export function mensagensDoErroSic(erro: HttpErrorResponse): string[] {
  const corpo = erro.error as Partial<ResponseApi<unknown>> | null;
  const lista = corpo?.errors?.length ? corpo.errors : corpo?.message ? [corpo.message] : [];

  return lista.filter((mensagem): mensagem is string => typeof mensagem === 'string' && mensagem.trim() !== '');
}
